// Assignments — four screens for who holds what: Admin, Data, Accounting,
// Approvals.
// ADMIN ONLY (owner, 2026-10-07: "Let's simplify Assignments by just making
// every assignment there admin-only."). Only an administrator sees the tab, and
// only an administrator appoints or removes anyone on it
// (`visibleAssignmentScreens` in `lib/identity.ts`; `App.tsx` gates the tab on
// `canOpenAssignments`).
//
//   Admin       — the Admin group, and nothing else since 2026-09-03 (owner:
//                 References, Rules and Analytics moved to Data, below).
//                 Admin-only.
//
//   Data        — References, Rules and Analytics (owner, 2026-09-03). They
//                 sat on the Admin screen from owner item 12 (2026-08-13),
//                 which put them there because appointing any of the three is
//                 an admin-only decision, same as appointing Admin itself.
//                 That reasoning is unchanged; only the tab they sit on is.
//                 STILL ADMIN-ONLY, on the SAME literal role test as Admin:
//                 nobody else sees this screen, even someone who holds one of
//                 the three groups on it — see `visibleAssignmentScreens`'s
//                 own comment for why that gate is not derived from the group
//                 list the way Accounting's is.
//                 References and Rules each render ONE SUB-SECTION PER TAB
//                 (owner item 29, 2026-08-28) — the same shape a company card
//                 has for its approval tiers, and for the same reason: both
//                 screens grew into a set of unrelated tabs, and a person may
//                 now be given the Vehicles master without the vendor list.
//                 The whole-screen role is NO LONGER APPOINTABLE here (owner,
//                 2026-08-31) — only the per-tab roles are offered — and it
//                 is retired server-side (D-275).
//   Accounting  — Import Control, Export Control, Closing Confirmation, Manual
//                 Entry, Denial Review. Internal-role groups; References
//                 moved out to Admin (above) on the same date.
//   Approvals   — one group per company: its director and department heads
//                 together (its AP/AR holder too, from issue 3, 2026-08-11,
//                 until AP/AR Approval merged into Import Control on
//                 2026-09-29 — D-215, D-221; its Fleet Manager until that seat
//                 was retired — D-275). Assignment-row groups. Oversight Approval no longer
//                 gets its own standalone box (issue 2, 2026-08-10) — it is
//                 company-scoped now, exactly like director and department-
//                 head, and joins each company's group. EXCEPT `Other`: the
//                 catch-all anchor gets no card of its own here (issue 1,
//                 2026-08-11) — every company, including the 21 the anchor
//                 covers through the catch-all expansion, already has its own
//                 card, so staffing the anchor directly has no remaining use
//                 case. `reloadCompanies` is where this is filtered; nothing
//                 about the anchor row or the expansion itself changed. Each
//                 card is now one sub-section per tier — Director and
//                 Department Head (owner item 13, 2026-08-13) — so a
//                 tier with zero holders is a visible "No one assigned."
//                 instead of an omission from a flat list (`GroupCard`,
//                 `Group.seatsByRole`).
//
// THE CONTROL IS LABELLED "Assign" (owner, 2026-08-10); the stored action is
// still `appoint`, an `assignment_action` enum value. Where this file says
// "appoint" it means the action and the authority check (`canAppoint`), not the
// button.
//
// Two mechanisms sit under the groups and the page deliberately hides the
// difference — a group is a group:
//   * COMPANY groups are `assignments` rows. The appointment row IS the
//     authority; there is nothing else to keep in step with it.
//   * Every other group is an internal step role (`user_roles`), granted and
//     revoked directly.
// Both go through the API and both return synchronously.
//
// Who may appoint mirrors the API's own check (`canAppoint` in
// `backend/src/domain/authority.ts`), so the page never offers a control the
// API would refuse: Admin may appoint anyone to any group, Admin included, and
// nobody else may appoint or remove anyone. SELF-APPOINTMENT IS ALLOWED (owner
// ruling, 2026-08-13), so the signed-in user is never filtered out of the
// person picker.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Toast,
  ToastTitle,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { AppDialog } from '../components/AppDialog'
import { WarningBanner } from '../components/WarningBanner'
import { useRoomyTableClass, useClickableRowClass, rowActivationProps } from '../components/tableDensity'
import { errorMessage } from '../api/errors'
import { PageTabs } from '../components/PageTabs'
import { NotificationRecipientsManager } from './NotificationRecipientsManager'
import { ScreenIntro } from '../components/ScreenIntro'
import { ErrorBanner } from '../components/States'
import { type EntityOption } from '../components/EntityPicker'
import { PeopleField, type PersonTag } from '../components/PeopleField'
import { useDialogFormStyles } from '../components/dialogForm'
import { ListFilters } from '../components/ListFilters'
import { DecisionDialog } from '../components/DecisionDialog'
import { FrozenWhileClosed } from '../components/FrozenWhileClosed'
import { ScreenActions } from '../components/ScreenActions'
import { SortableHeaderCell } from '../components/SortableHeaderCell'
import { sortRows, useTableSort } from '../lib/tableSort'
import {
  ACCOUNTING_STEP_GROUPS,
  ADMIN_STEP_GROUPS,
  DATA_STEP_GROUPS,
  DEFAULT_PATH_APPROVAL_ROLES,
  ASSIGNMENT_ACTION,
  ASSIGNMENT_ROLES,
  ASSIGNMENT_SCREEN_TITLE,
  GRANTABLE_ROLES,
  ANALYTICS_ASSIGNMENT_ROLES,
  ANALYTICS_SECTIONS,
  ANALYTICS_SECTION_ASSIGNMENT_ROLE,
  ORG_WIDE_COMPANY_LABEL,
  REFERENCE_SECTION_ROLE,
  REFERENCE_SECTION_ROLE_LABEL,
  RULES_SECTIONS,
  RULES_SECTION_LABEL,
  RULES_SECTION_ROLE,
  SCREEN_SUBHEADER,
  TOASTER_ID,
  assignmentRoleLabel,
  tokenForRole,
  type AssignmentRole,
  type ReferenceSection,
  type Role,
  type StepGroupSpec,
  type Workspace,
} from '../lib/constants'
import {
  ALL_COMPANIES,
  alreadyHolds,
  noApproverWarning,
  bulkSavedToast,
  peopleSavedToast,
  removalSentences,
  personKey,
  refusedMessage,
  rowLabel,
  rowName,
  rowsOf,
  type AssignmentRow,
  type Group,
  type RoleSeats,
  type Seat,
  type Target,
  unlessAlreadyHeld,
} from '../lib/assignmentRows'
import type { Assignment, DirectoryUser, UserRole } from '../lib/types'
import { assignmentScreenReads, visibleAssignmentScreens, type AssignmentScreen, type Session } from '../lib/identity'
import { PENDING, pageState, type ReadState } from '../lib/readState'
import { formatDate, sentenceList } from '../lib/format'
import {
  createAssignment,
  grantUserRole,
  listAssignments,
  listUserRoles,
  loadCompaniesForManagement,
  revokeUserRole,
  searchPeople,
} from '../lib/data'

/** The one workspace. Kept as a constant rather than a control (a one-option
    dropdown is furniture), but the row filter stays so a historical row for
    another workspace is still excluded. */
const WS: Workspace = 'invoice'

// A stable reference for "not loaded yet" — a fresh `[]` literal at the call
// site would be a new array every render, which is what fed a `useMemo`
// dependency array below and defeated its memoization.
const EMPTY_COMPANIES: { id: string; name: string }[] = []

// ---- References / Rules sub-roles (owner item 29, 2026-08-28) --------------
//
// References and Rules grew into screens of unrelated tabs, so the owner asked
// for the same granularity the company blocks already have: a role PER TAB,
// granted here, and a user sees only the tabs they hold one for — and sees the
// screen at all only if they hold at least one (`canOpenReferences` /
// `canOpenRules` in `lib/identity.ts`, which `App.tsx` gates the nav on).
//
// THE ONLY GRANT. The whole-screen `references` and `rules` roles left this
// screen on 2026-08-31 and were retired on 2026-10-03 (D-275); their holders
// were given every per-tab role of the same screen.
//
// SECURITY. This screen decides what is OFFERED, never what is permitted:
// `canManageReferenceSection` / `canManageRulesSection` in
// `backend/src/domain/authority.ts` gate every write route independently, so a
// hidden tab and a refused write are two separate facts and only the second is
// the boundary.


/** A block's per-tab sub-roles, in tab order, keyed by the block's coarse
    role. Any group NOT listed here is a single role and renders the flat list
    it always had. */
/** One appointable sub-row inside a block — ONE ROW PER TAB.
 *
 *  `roles` is the set the row grants and `role` is the first of them: the row's
 *  identity, what its key and its authority check read. Almost every row grants
 *  exactly one, so the two are the same value.
 *
 *  Every References row grants exactly one since 2026-09-29 (owner, D-199):
 *  the Additional Detail tab's table is "Additional Detail - Marketing
 *  Categories" (its Transportation Categories row went with that table on
 *  2026-10-19, D-381), and the fleet row reads "Associated Entities -
 *  Vehicles". Each row names the tab and then the table it opens. The
 *  one-row-for-two grant of D-193 is gone; `roles` stays for a future row
 *  that needs it. */
interface BlockSubRole {
  role: Role
  roles: Role[]
  label: string
}

const one = (section: ReferenceSection): BlockSubRole => ({
  role: REFERENCE_SECTION_ROLE[section],
  roles: [REFERENCE_SECTION_ROLE[section]],
  label: REFERENCE_SECTION_ROLE_LABEL[section],
})

const BLOCK_SUB_ROLES: Partial<Record<Role, BlockSubRole[]>> = {
  // ONE ROW PER TABLE, in the References screen's own tab order (owner,
  // 2026-09-15: "obviously the Assignments tab should match"; one row per
  // table since 2026-09-29, D-199).
  References: [
    one('vendors'),
    one('companies'),
    one('codes'),
    one('marketing'),
    one('vehicles'),
  ],
  Rules: RULES_SECTIONS.map((section) => ({
    role: RULES_SECTION_ROLE[section],
    roles: [RULES_SECTION_ROLE[section]],
    label: RULES_SECTION_LABEL[section],
  })),
  // Analytics is not here since 2026-10-01 (D-258): its Dashboard and
  // Reporting are per-company APPOINTMENTS, listed by `analyticsGroups`.
}

const useStyles = makeStyles({
  // Title + subheader on one baseline, as every pipeline screen does it.
  groups: { display: 'flex', flexDirection: 'column', gap: '14px' },
  group: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
  },
  trash: { color: tokens.colorBrandForeground1 },
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },

  // ---- ONE TABLE FOR ALL FOUR SCREENS (2026-09-14) ---------------------
  //
  // Every Assignments screen is the same shape underneath: a set of THINGS you
  // staff, each holding a set of ROLES, each holding zero or more people. A
  // company holds Director / Manager; the References block
  // holds its six tabs; Import Control holds exactly one role, itself.
  //
  // The first build of this table pivoted the roles into COLUMNS, which works
  // beautifully for Company Roles — where 38 rows share one set of four — and
  // not at all for Solution Management, whose three blocks share no roles with
  // each other. That forced "a table here, cards there", which is not a design,
  // it is two designs. Roles are ROWS instead, under the thing they belong to,
  // and every screen gets the same three columns and the same interactions.
  //
  // The columns are: what is being staffed, who holds it, and the one control
  // that changes it.
  atScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  atTable: { width: '100%', minWidth: '720px', tableLayout: 'fixed' },
  atCell: { verticalAlign: 'top' },
  atRole: { fontWeight: tokens.fontWeightSemibold },
  atNote: { color: tokens.colorNeutralForeground3, display: 'block' },
  // "All" is a real value, not a missing one — a role that is not scoped to a
  // company is scoped to every one of them — but it is the same value on every
  // centralized row, so it recedes rather than competing with the company names
  // beside it.
  atAll: { color: tokens.colorNeutralForeground3 },
  atSeats: { display: 'flex', flexDirection: 'column', gap: '4px' },
  atSeat: { display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' },
  atSeatEnded: { opacity: 0.55 },
  dlgPeriod: { display: 'block', color: tokens.colorNeutralForeground3 },
  atEmail: { color: tokens.colorNeutralForeground3 },
  // A VACANT ROLE IS THE CONTROL, where the viewer may fill it: the gap is what
  // someone came to this screen about, so it is the thing that opens the
  // dialog — with the thing and the role already chosen. Where they may not, it
  // is plain text, because a control that refuses is worse than none.
  //
  // The word is "Vacant", not "Assign": on a roster that is mostly empty, an
  // action word in every empty cell paints a wall of identical buttons and
  // buries the names among them. The row says what it IS and becomes visibly a
  // control on hover and on focus.
  atVacant: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // The missing-approver sentence, on the row of each empty default-path seat
  // (D-336, amended 2026-10-09). The banner above counts the companies; this
  // says which seat. Not the yellow warning treatment at row level —
  // twenty yellow rows would be a wall rather than a warning.
  atFlag: { display: 'block', marginTop: '2px', color: tokens.colorPaletteRedForeground1 },
  // THE ONE URGENT FACT ON THIS SCREEN — a company missing a Manager Approval
  // or Director Approval approver has its invoices wait at that empty step
  // (D-336) — is said once
  // at the top, in `components/WarningBanner.tsx` (the box was this screen's
  // own until 2026-09-30, D-241, when References → Vendors needed it too).
  atStep: { fontVariantNumeric: 'tabular-nums', color: tokens.colorNeutralForeground2 },
  // THE INVOICES TAB'S OWN TWO ROWS (owner, 2026-09-14): an action bar with the
  // count on the left and the action on the right, then the list's own toolbar
  // with a labelled Select all. Same shape, same spacing, same order as
  // `ApprovalScreen`'s `actionBar` and `InvoiceQueue`'s `toolbar`.
  // No `minHeight`: the strip has no height when it does not render, and
  // reserving the space would defeat the point of hiding it (owner,
  // 2026-09-15).
  selBar: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', padding: '8px 0' },
  /** The sentence under each bulk picker, naming what the action reaches. */
  bulkScope: { display: 'block', color: tokens.colorNeutralForeground3 },
  // The holders inside the row's own editor, each with the Remove the table
  // used to carry in a column of its own.
  dlgSeats: { display: 'flex', flexDirection: 'column', gap: '4px' },
  dlgSeat: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '4px 8px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  hint: { color: tokens.colorNeutralForeground3, display: 'block', marginTop: '4px' },
  // `overflow: visible` matters: the staff picker's listbox renders INLINE (it
  // must stay inside the dialog's focus trap), so a clipping ancestor cuts the
  // dropdown off instead of letting it overlay. The form is two fields tall, so
  // nothing here needs to scroll.
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '360px',
    overflow: 'visible',
  },
})

/** WHAT EVERY ASSIGNMENTS SCREEN OPENS SORTED BY, and returns to on every tab
    change: Role (owner, 2026-10-09: "By default, Invoices should be sorted by
    Role ... Analytics should be sorted by Role. References should be sorted by
    Role. Rules should be sorted by Role. Assignments should be sorted by
    Role."). On Invoices a role reads "Step 1: Import Control", and the step
    sorts numerically, so Role still reads the pipeline start to finish. */
const ROLE_SORT = { key: 'role', direction: 'asc' } as const

/** "Step 1: Import Control" on a pipeline step's row (owner, 2026-10-09: "drop
    the Step column but change the Role column values to be like 'Step 1:
    Import Control'"); the role alone on every other row. */
function roleLabel(row: AssignmentRow): string {
  return row.step === undefined ? row.role : `Step ${row.step}: ${row.role}`
}



/** WHAT "ARE YOU SURE?" SAYS (B-07), once per Apply and only when the Apply
    removes someone (D-341): who goes, from where, and who is added alongside. */
function confirmSentence(removed: readonly string[], added: readonly string[], from: string): string {
  const head = `${sentenceList(removed)} will be removed from ${from}.`
  if (added.length === 0) return head
  return `${head} ${sentenceList(added)} will be added.`
}

/** The directory record an add writes. */
function userOf(u: DirectoryUser): { id: string; upn: string; name: string; email: string } {
  return { id: u.id, upn: u.upn, name: u.display_name || u.upn, email: u.mail ?? u.upn }
}

/** One person across the rows of a bulk edit, with every seat they hold there. */
interface BulkPerson {
  key: string
  name: string
  email?: string
  person?: Seat['person']
  seats: { row: AssignmentRow; seat: Seat }[]
  rowKeys: Set<string>
}

function isLive(a: Assignment): boolean {
  return !!a.is_active && !a.retired_at
}

/**
 * WHAT AN ENDED ASSIGNMENT SAYS (owner, 2026-09-14: "'Show retired and rejected'
 * is not approved language. What is this? I would expect an assignment to have a
 * start and end datetime - not be retired, and I don't know of any rejected
 * status").
 *
 * They were right on both counts, and the data already agrees with them: an
 * assignment row carries `created_at` and `retired_at`, which ARE a start and an
 * end. "Retired" was the column name leaking into the copy. So a row that has
 * ended now says when it ended, and one still running says nothing at all —
 * being current is the normal case and needs no badge.
 *
 * AND THERE IS NO REJECTED STATE IN THIS SYSTEM. `assignment_request_status`
 * still carries `Pending` and `Rejected` because the Dataverse predecessor wrote
 * a Pending row for an asynchronous broker to validate and flip. This port's
 * endpoint validates first and writes the outcome, so an appointment is created
 * at `Applied` and a refusal is an HTTP error that writes no row at all
 * (`backend/src/http/routes/assignments.ts`'s header says so). Neither value can
 * reach this screen, so neither is named on it.
 */
function startedOn(a: Assignment): string | undefined {
  return a.created_at ? `From ${formatDate(a.created_at)}` : undefined
}

/** The Notifications tab's line under the tabs, in the owner's words (2026-10-07). */
const NOTIFICATIONS_TAB_INTRO = 'Send notifications to required personnel based on fixed criteria.'

/** The Approvers dropdown's one narrowing value: the companies the warning counts. */
const NO_APPROVERS = 'none'

/** The screens whose every row is organization-wide and still carry a Company
    column reading `All` (owner, 2026-10-07: "At Assignments > References,
    Rules, and Assignments, add to the table a Company column that just says
    'All'. I just want to standardize the table structures here a bit
    better."). Partly reverses D-135, which had dropped the column there. */
const ALL_COMPANY_SCREENS: readonly AssignmentScreen[] = ['references', 'rules', 'assignments']

interface Props {
  session: Session
}

export function AssignmentsScreen({ session }: Props) {
  const s = useStyles()
  const f = useDialogFormStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)

  const visibleScreens = useMemo(() => visibleAssignmentScreens(session), [session])
  // `null` only ever surfaces transiently, IF at all: `session` is set once,
  // in App.tsx's mount effect, and nothing today replaces it for a mounted
  // component, so `visibleScreens` is referentially stable for this
  // component's whole lifetime and the re-sync effect below never actually
  // fires. It stays defensive rather than being deleted: wiring a session
  // re-derive in later (a role change without a new sign-in, say) must not
  // strand this component on a screen the new session no longer holds
  // anything on, and this is what would catch that the day it happens.
  const [activeScreen, setActiveScreen] = useState<AssignmentScreen | null>(visibleScreens[0] ?? null)

  // Re-sync whenever the visible set changes and the active screen falls
  // outside it — otherwise a TabList could sit with `selectedValue` matching
  // no `Tab`. Defensive only today (see the note above); this is what makes
  // that safe to add later without a second thought here.
  useEffect(() => {
    if (activeScreen !== null && visibleScreens.includes(activeScreen)) return
    setActiveScreen(visibleScreens[0] ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleScreens])


  const [search, setSearch] = useState('')
  // Multi-selects (owner, 2026-10-14): an empty pick does not narrow, and a
  // row passes when it matches any pick.
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [companyFilter, setCompanyFilter] = useState<string[]>([])
  // ASSIGNED TO (owner, 2026-09-14) — narrows to the rows one person holds a
  // LIVE seat on. Keyed on the seat's own `key`? No: on the NAME, because the
  // same person holds separate seats on separate rows and the question is
  // "what does Dana hold", not "where is this one grant". Retired and rejected
  // seats are excluded for the same reason the table dims them — they are
  // history, not authority — so a person who once held a row and no longer
  // does is not offered as an answer.
  const [personFilter, setPersonFilter] = useState<string[]>([])
  // APPROVERS (owner, 2026-10-07): `NO_APPROVERS` narrows Invoices to exactly
  // the companies the missing-approver warning counts; `''` is any. Set by the
  // warning's Show These Companies button, the way References → Vendors' button
  // sets its ZIP Code dropdown, and cleared from the same Filter popover.
  const [approverFilter, setApproverFilter] = useState('')
  // THE THREE DROPDOWNS RESET WITH THE SCREEN. Their options are that screen's
  // own roles and companies, so a selection carried across has nothing to match
  // and empties the new screen without saying why — filtering to PACE
  // Healthcare Transportation on Invoices and then opening References, where
  // every row reads `All`, showed a blank page and a Filter button with a 1 on
  // it. The SEARCH TERM is deliberately kept: it is screen-independent, and the
  // cross-screen hint below exists precisely so a term can be carried.

  const [assignmentsState, setAssignmentsState] = useState<ReadState<Assignment[]>>(PENDING)
  const [userRolesState, setUserRolesState] = useState<ReadState<UserRole[]>>(PENDING)
  // Approvals' THIRD independent read — the company master — held as a
  // `ReadState` exactly like the other two. It used to be a plain array
  // defaulted on failure with `.catch(() => setCompanies([]))`, which is a
  // false EMPTY, not a modeled failure: a transient `GET /companies` error left
  // a director's own company silently missing, reporting `ready` with no error
  // and no retry — precisely what the three-state model exists to rule out.
  const [companiesState, setCompaniesState] = useState<ReadState<{ id: string; name: string }[]>>(
    PENDING,
  )
  // THERE IS NO SCREEN-LEVEL WRITE BANNER ANY MORE (QA wave B1, B1-05). Every
  // write on this screen happens inside a dialog — a row's People Field or
  // the bulk edit's (D-341) — so a refusal painted on the screen
  // behind the modal was dimmed by the overlay while the attempt that caused
  // it was still open, and then outlived it: the last-administrator guard was
  // still on screen after two later successful writes. `dialogError` is where
  // a refusal goes, and it dies with its dialog. The READ states above are
  // unaffected; they replace the groups area rather than bannering over it.

  // The row editor. `manageRow` is the row it opened on; `target` is what its
  // adds write through. A row the viewer may not appoint into still opens, to
  // be read, and has no `target`.
  const [target, setTarget] = useState<Target | null>(null)
  const [manageRow, setManageRow] = useState<AssignmentRow | null>(null)
  // Rows ticked for the bulk edit. Keys, not rows: the row objects are rebuilt
  // on every render from the group model, and a Set of them would never match.
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<string>>(() => new Set())
  /** EDIT N SELECTED (owner, 2026-10-09 — D-341): one pop-up that adds and
      removes across the ticked rows, where Assign to N Selected and Unassign
      From N Selected were two, and neither showed who held what. */
  const [bulkOpen, setBulkOpen] = useState(false)
  /** THE STAGED CHANGES (D-341), shared by the two pop-ups since only one is
      open at a time. People to add; people to take off — a seat key in a row's
      editor, a person key in the bulk one; and, in the bulk one, partial
      holders to add to the rest of the selected rows. Nothing is written until
      Apply, so adding three people is one trip rather than three. */
  const [adds, setAdds] = useState<DirectoryUser[]>([])
  const [drops, setDrops] = useState<ReadonlySet<string>>(() => new Set())
  const [completes, setCompletes] = useState<ReadonlySet<string>>(() => new Set())
  /** "Are you sure?" (B-07, D-171) — asked ONCE per Apply, and only when the
      Apply removes someone (D-341); it used to be asked per person. The
      sentence is held apart from whether it is open so it cannot change on its
      way out — the `shown` pattern `HoldControl` uses. */
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  // Sorted the way References and Rules sort: click a header. The state lives
  // here rather than in the table so it survives a tab change — Step, Role and
  // Company mean the same thing on every screen that has them.
  //
  // IT OPENS ON STEP, ascending, which is the whole reason the Step column
  // exists (owner, 2026-09-14: "I want to easily be able to read the pipeline
  // from start to finish, and alphabetizing does not cover that"). The other
  // four screens have no Step column and fall back to the order `rowsOf` builds,
  // which is their own tab order — also not alphabetical, and also right.
  //
  // AND IT RESETS WITH THE SCREEN, like the filters below. It did not for one
  // round, on the reasoning that Step / Role / Company mean the same thing
  // everywhere — but sorting References by Role and coming back left Invoices
  // sorted by Role too, which is not the default the Step column exists to
  // provide (owner, 2026-09-14: "The default sorting for Invoices should be
  // Step").
  const sort = useTableSort<string>(ROLE_SORT)
  const resetSort = sort.reset
  useEffect(() => {
    setRoleFilter([])
    setCompanyFilter([])
    setApproverFilter('')
    // And the ticks, which name rows that do not exist on the new screen.
    setSelectedRows(new Set())
    // Invoices back to Step; the other four to their own build order, which is
    // their tab order and is what "unsorted" means for them.
    //
    // `sort.reset` ALONE in the dependency list, never `sort` — the hook returns
    // a fresh object whenever the sort state changes, so depending on the whole
    // thing re-ran this effect on every header click and put the sort straight
    // back to Step. `reset` is a stable `useCallback` and is the only part of it
    // this effect uses.
    resetSort(ROLE_SORT)
  }, [activeScreen, resetSort])
  // The dialog surface element, held in STATE rather than a ref because the
  // picker below has to re-render once it exists — it mounts its listbox there,
  // which is what keeps the options above the dialog's own fields.
  const [dialogSurface, setDialogSurface] = useState<HTMLDivElement | null>(null)
  // The last directory search's results. An appointment carries the user
  // principal name as well as the id, so the picked option is resolved back to
  // its whole record. State, not a ref: the search runs from the field's props.
  const [people, setPeople] = useState<DirectoryUser[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)

  // ---- Authority (mirrors the API's own check) -------------------------------
  const isAdmin = (session.rolesByWs[WS] ?? []).includes('Admin')

  const reloadAssignments = useCallback(async () => {
    try {
      const data = await listAssignments()
      setAssignmentsState({ kind: 'loaded', data })
    } catch (e) {
      setAssignmentsState({ kind: 'failed', error: `Could not load assignments: ${errorMessage(e)}` })
    }
  }, [])

  const reloadUserRoles = useCallback(async () => {
    try {
      const data = await listUserRoles()
      setUserRolesState({ kind: 'loaded', data })
    } catch (e) {
      setUserRolesState({ kind: 'failed', error: `Could not load roles: ${errorMessage(e)}` })
    }
  }, [])

  const reloadCompanies = useCallback(async () => {
    try {
      const cs = await loadCompaniesForManagement()
      setCompaniesState({
        kind: 'loaded',
        data: cs
          .filter((c) => c.is_active)
          // NO ANCHOR FILTER (D-134, 2026-09-22). Two rows had to be dropped
          // here until then — `Other` and `All` — because they were in
          // `companies` without being companies, and a block for either would
          // have offered Director and Manager seats on a row that named
          // no company. Both rows are gone, so every company this list carries
          // gets a block, which is what "every company should get its own block
          // of assignments" asks for.
          .map((c) => ({ id: c.company_id, name: c.company_name }))
          .filter((c) => c.name)
          .sort((a, b) => a.name.localeCompare(b.name)),
      })
    } catch (e) {
      setCompaniesState({ kind: 'failed', error: `Could not load companies: ${errorMessage(e)}` })
    }
  }, [])

  // Every screen reads the roster: only an administrator opens this tab, and
  // both lists are admin-only on the server (code review G-05). Invoices also
  // reads the assignments and the companies, because its centralized step
  // roles are `user_roles` and its per-company tiers are assignments.
  const needsApproval = visibleScreens.includes('invoices')

  useEffect(() => {
    reloadUserRoles()
    if (needsApproval) {
      reloadAssignments()
      reloadCompanies()
    }
  }, [needsApproval, reloadUserRoles, reloadAssignments, reloadCompanies])

  const toast = (title: string, intent: 'success' | 'error' = 'success') =>
    dispatchToast(<Toast><ToastTitle>{title}</ToastTitle></Toast>, { intent })

  // ---- Writes ---------------------------------------------------------------
  //
  // THE WRITE AND ITS ANNOUNCEMENT ARE SEPARATE (QA wave B1, B1-10). A seat's
  // `remove` used to be the whole handler — busy flag, reload, toast — and the
  // bulk Unassign path calls that same remover once per row before reporting
  // one summary of its own, so unassigning one person from two rows raised
  // three toasts: "Gray Petrov was removed from 2 roles." followed by "The
  // role was removed." twice. The removers below write and nothing else; each
  // caller says what it did. It also makes the bulk loop honest — the grouped
  // role remover returned `void` and its `await` awaited nothing, so the count
  // it reported was optimistic.

  /** The write alone. Throws on refusal; announces nothing; reloads nothing. */
  async function revokeRoleRow(row: UserRole): Promise<void> {
    await revokeUserRole(row.id)
  }

  /** The write alone. Retiring never deletes: the row is stamped with its
      retirement time and kept, because the audit trail is the point. */
  async function deactivateAssignment(a: Assignment): Promise<void> {
    await createAssignment({
      workspace: WS,
      action: ASSIGNMENT_ACTION.retire,
      role: a.role,
      company_id: a.company_id ?? null,
      user_id: a.user_id,
      user_upn: a.user_upn,
      user_name: a.user_name ?? null,
      target_assignment_id: a.id,
    })
  }

  function resetStaged() {
    setAdds([])
    setDrops(new Set())
    setCompletes(new Set())
    setDialogError(null)
  }

  /** THE ROW IS THE CONTROL (owner, 2026-09-14: "I should be able to click a row
      to manage its assignments, like in References and Rules"). It opens the
      row's People Field (D-341). A row the viewer may not appoint into still
      opens: the holders are worth reading, and the field offers no search. */
  function openManage(row: AssignmentRow) {
    resetStaged()
    setManageRow(row)
    setTarget(row.fill ?? null)
  }

  function closeDialog() {
    setConfirmOpen(false)
    setTarget(null)
    setManageRow(null)
  }

  function openBulk() {
    resetStaged()
    setBulkOpen(true)
  }

  function closeBulk() {
    setConfirmOpen(false)
    setBulkOpen(false)
  }

  function searchStaff(t: string): Promise<EntityOption[]> {
    return searchPeople(t).then<EntityOption[]>((found) => {
      setPeople(found)
      return found.map((u) => ({
        id: u.id,
        primary: u.display_name || u.upn,
        secondary: [u.job_title, u.department].filter(Boolean).join(' · ') || u.upn,
      }))
    })
  }

  /**
   * GIVE ONE PERSON ONE ROW, through the row's OWN target, so the authority
   * mirror applies per row exactly as it always has. Shared by the row editor
   * and the bulk edit.
   *
   * ALREADY HELD IS A SKIP, not a refusal (owner, 2026-09-29): adding someone to
   * ten rows when they hold three already is seven adds, not three failures.
   * `grantRoles` is the row's whole set — the Transportation row was two roles —
   * so a person holding half of it is given the other half. Throws on refusal.
   *
   * THE SERVER'S "ALREADY HOLDS" IS A SKIP TOO (owner, 2026-10-14: "This kind
   * of scenario is not really an error"). `alreadyHolds` reads only the rows
   * this screen loaded, so a seat it has not seen goes to the server, which
   * answers 409 `duplicate_key` — the same fact, found one step later.
   */
  async function appointOne(
    t: Target,
    user: { id: string; upn: string; name: string; email: string },
    label: string,
  ): Promise<'added' | 'skipped'> {
    const held = { assignments: assignmentRows ?? [], userRoles: roleRows }
    if (t.kind === 'role') {
      const granted = t.roleChoices?.[0] ?? t.role
      const grantAll = t.grantRoles && t.grantRoles.includes(granted) ? t.grantRoles : [granted]
      const tokens = grantAll.map((role) => {
        const token = tokenForRole(role)
        if (!token) throw new Error(`${label} cannot be granted here.`)
        return token
      })
      const missing = tokens.filter((token) => !alreadyHolds(held, user.id, WS, { kind: 'role', token }))
      if (missing.length === 0) return 'skipped'
      // Sequential: there is no batch route, and a partial failure surfaces as
      // the API's own refusal with whatever landed reloaded afterwards.
      let added = false
      for (const token of missing) {
        const landed = await unlessAlreadyHeld(() =>
          grantUserRole({
            role: token,
            user_id: user.id,
            user_upn: user.upn,
            user_name: user.name,
            user_email: user.email,
          }),
        )
        added = added || landed
      }
      return added ? 'added' : 'skipped'
    }
    const role = t.roleChoices[0]
    if (alreadyHolds(held, user.id, WS, { kind: 'assignment', role, companyId: t.companyId ?? null })) return 'skipped'
    const landed = await unlessAlreadyHeld(() =>
      createAssignment({
        workspace: WS,
        action: ASSIGNMENT_ACTION.appoint,
        role,
        company_id: t.companyId ?? null,
        user_id: user.id,
        user_upn: user.upn,
        user_name: user.name,
      }),
    )
    return landed ? 'added' : 'skipped'
  }

  /**
   * A PERSON PICKED in either pop-up. `heldKey` names the tag they already
   * have here, if any: someone staged for removal comes back instead of being
   * added twice, and someone already there is told so (owner, 2026-09-29: "I am
   * able to add the same person to the same role more than once").
   */
  function stageAdd(option: EntityOption, heldKey: (user: DirectoryUser) => string | null) {
    const user = people.find((p) => p.id === option.id)
    if (!user) return
    setDialogError(null)
    if (adds.some((u) => u.id === user.id)) return
    const key = heldKey(user)
    if (key !== null) {
      if (drops.has(key)) {
        undoRemove(key)
        return
      }
      setDialogError(`${userOf(user).name} already holds this role here.`)
      return
    }
    setAdds((prev) => [...prev, user])
  }

  /** ✕ on a tag: a staged add is simply dropped; a holder is staged to go. */
  function stageRemove(key: string) {
    if (key.startsWith('new:')) {
      setAdds((prev) => prev.filter((u) => `new:${u.id}` !== key))
      return
    }
    setCompletes((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
    setDrops((prev) => new Set(prev).add(key))
  }

  function undoRemove(key: string) {
    setDrops((prev) => {
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }

  /**
   * SAVE THE ROW'S PEOPLE FIELD (D-341): every staged removal, then every
   * staged add, one write each. A refusal keeps the pop-up open with the reason
   * IN it (QA wave B1, B1-05) and only the refused changes still staged; the
   * rest has landed and is reloaded.
   */
  async function saveRow(confirmed = false) {
    const row = liveManageRow
    if (!row) return
    const removals = row.seats.filter((m) => drops.has(m.key) && m.canRemove && m.remove)
    const addedNames = adds.map((u) => userOf(u).name)
    if (removals.length === 0 && adds.length === 0) {
      closeDialog()
      return
    }
    if (removals.length > 0 && !confirmed) {
      setConfirmText(confirmSentence(removals.map((m) => m.name), addedNames, rowName(row)))
      setConfirmOpen(true)
      return
    }
    setConfirmOpen(false)
    setDialogError(null)
    setSubmitting(true)
    const byKind = { roles: 0, assignments: 0 }
    const added: string[] = []
    const failures: string[] = []
    const keptDrops = new Set<string>()
    const keptAdds: DirectoryUser[] = []
    for (const m of removals) {
      try {
        await m.remove?.()
        if (m.kind === 'role') byKind.roles += 1
        else byKind.assignments += 1
      } catch (e) {
        failures.push(`${m.name}: ${errorMessage(e)}`)
        keptDrops.add(m.key)
      }
    }
    for (const u of adds) {
      try {
        if (!target) throw new Error('You do not have permission to change this assignment.')
        if ((await appointOne(target, userOf(u), row.role)) === 'added') added.push(userOf(u).name)
      } catch (e) {
        failures.push(`${userOf(u).name}: ${errorMessage(e)}`)
        keptAdds.push(u)
      }
    }
    await Promise.all([reloadUserRoles(), reloadAssignments()])
    setSubmitting(false)
    const landed = peopleSavedToast(added, rowName(row), byKind)
    if (failures.length > 0) {
      setDrops(keptDrops)
      setAdds(keptAdds)
      setDialogError(refusedMessage(landed, failures))
      return
    }
    closeDialog()
    toast(landed)
  }

  function selectedRowsForBulk(): AssignmentRow[] {
    return rowsOf(currentGroups).filter((r) => selectedRows.has(r.key) && r.fill)
  }

  /**
   * EVERYONE WHO HOLDS ANY OF THE SELECTED ROWS, one entry per person, with
   * the seats they hold there (D-341). The bulk pop-up shows them all: a
   * person on every row as a plain tag, one on some as a dashed "2 of 5" tag.
   * Unassign From N Selected offered only the people common to every row,
   * which hid exactly the gaps a bulk edit is opened to fix.
   */
  function bulkPeople(rows: readonly AssignmentRow[]): BulkPerson[] {
    const byPerson = new Map<string, BulkPerson>()
    for (const row of rows) {
      for (const seat of row.seats) {
        if (!seat.live) continue
        const key = personKey(seat)
        const entry = byPerson.get(key) ?? {
          key,
          name: seat.name,
          email: seat.email,
          person: seat.person,
          seats: [],
          rowKeys: new Set<string>(),
        }
        entry.seats.push({ row, seat })
        entry.rowKeys.add(row.key)
        byPerson.set(key, entry)
      }
    }
    return [...byPerson.values()].sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * SAVE THE BULK EDIT (D-341). Removals first — each seat through its own
   * `remove`, so every revoke carries the authority check and the audit it
   * would have had one at a time. Then adds, row by row through each row's own
   * target. ONE ROW'S FAILURE DOES NOT ABANDON THE REST: there is no bulk
   * endpoint and no transaction, so every row is attempted and the message says
   * what landed and what was refused.
   */
  async function saveBulk(confirmed = false) {
    const rows = selectedRowsForBulk()
    if (rows.length === 0) return
    const people = bulkPeople(rows)
    const dropping = people.filter((p) => drops.has(p.key))
    const completing = people.filter((p) => completes.has(p.key) && !drops.has(p.key) && p.person)
    if (dropping.length === 0 && completing.length === 0 && adds.length === 0) {
      closeBulk()
      return
    }
    const addingNames = [...completing.map((p) => p.name), ...adds.map((u) => userOf(u).name)]
    if (dropping.length > 0 && !confirmed) {
      // Each person from the rows they hold, not the whole selection.
      const removing = removalSentences(
        dropping.map((p) => ({ name: p.name, from: rows.filter((r) => p.rowKeys.has(r.key)).map(rowName) })),
      )
      setConfirmText(addingNames.length > 0 ? `${removing} ${sentenceList(addingNames)} will be added.` : removing)
      setConfirmOpen(true)
      return
    }
    setConfirmOpen(false)
    setDialogError(null)
    setSubmitting(true)
    const byKind = { roles: 0, assignments: 0 }
    const failures: string[] = []
    for (const p of dropping) {
      for (const { row, seat } of p.seats) {
        if (!seat.canRemove || !seat.remove) continue
        try {
          await seat.remove()
          if (seat.kind === 'role') byKind.roles += 1
          else byKind.assignments += 1
        } catch (e) {
          failures.push(`${rowName(row)}: ${errorMessage(e)}`)
        }
      }
    }
    const addedRows = new Map<string, number>()
    let skipped = 0
    const toAdd = [
      ...completing.map((p) => ({ user: p.person as NonNullable<Seat['person']>, has: p.rowKeys })),
      ...adds.map((u) => ({ user: userOf(u), has: new Set<string>() })),
    ]
    for (const { user, has } of toAdd) {
      let n = 0
      for (const row of rows) {
        if (has.has(row.key)) continue
        try {
          const outcome = await appointOne(row.fill as Target, user, row.role)
          if (outcome === 'added') n += 1
          else skipped += 1
        } catch (e) {
          failures.push(`${rowName(row)}: ${errorMessage(e)}`)
        }
      }
      if (n > 0) addedRows.set(user.name, n)
    }
    await Promise.all([reloadUserRoles(), reloadAssignments()])
    setSubmitting(false)
    const landed = bulkSavedToast(addedRows, byKind, skipped)
    if (failures.length > 0) {
      // Everything that landed is reloaded; start the field again from there.
      resetStaged()
      setDialogError(refusedMessage(landed, failures))
      return
    }
    closeBulk()
    setSelectedRows(new Set())
    toast(landed)
  }

  // ---- Group assembly -------------------------------------------------------

  const userRoles = userRolesState.kind === 'loaded' ? userRolesState.data : null
  const assignmentRows = assignmentsState.kind === 'loaded' ? assignmentsState.data : null
  const companies = companiesState.kind === 'loaded' ? companiesState.data : EMPTY_COMPANIES

  const roleRows = useMemo(
    () => (userRoles ?? []).filter((r) => r.workspace === WS && r.is_active !== false),
    [userRoles],
  )

  const roleGroup = useCallback(
    (spec: StepGroupSpec): Group => {
      /**
       * Who holds ANY of these roles, one entry per PERSON.
       *
       * A row that grants two (Vehicles & Transportation) would otherwise list
       * someone twice — once per underlying `user_roles` row — and offer two
       * Remove buttons that each took half their access away. Deduped on the
       * person, and the seat's Remove revokes every row behind it, so the
       * button does what the row says it does.
       */
      const seatsFor = (...roles: Role[]): Seat[] => {
        const tokens = roles
          .map((role) => tokenForRole(role))
          .filter((t): t is NonNullable<ReturnType<typeof tokenForRole>> => !!t)
        const byPerson = new Map<string, { name: string; email?: string; rows: UserRole[] }>()
        for (const r of roleRows.filter((row) => (tokens as string[]).includes(row.role))) {
          const person = r.user_object_id || r.user_upn || r.id
          const entry = byPerson.get(person) ?? {
            name: r.user_name || r.user_upn || r.user_object_id,
            email: r.user_email || r.user_upn,
            rows: [],
          }
          entry.rows.push(r)
          byPerson.set(person, entry)
        }
        return [...byPerson.entries()]
          .map(([person, entry]) => ({
            key: `${person}:${tokens.join('+')}`,
            name: entry.name,
            email: entry.email,
            live: true,
            canRemove: isAdmin,
            kind: 'role' as const,
            remove: async () => {
              for (const row of entry.rows) await revokeRoleRow(row)
            },
            person: {
              id: entry.rows[0].user_object_id,
              upn: entry.rows[0].user_upn,
              name: entry.name,
              email: entry.rows[0].user_email || entry.rows[0].user_upn,
            },
          }))
          .sort((x, y) => x.name.localeCompare(y.name))
      }
      const token = tokenForRole(spec.role)
      // References and Rules carry one sub-role PER TAB (owner item 29,
      // 2026-08-28), rendered as sub-sections inside the block exactly the way
      // a company card renders one per approval tier. Every other group is a
      // single role and keeps the flat list it always had.
      const subRoles = BLOCK_SUB_ROLES[spec.role]
      const ownSeats = seatsFor(spec.role)
      // A block with per-tab rows is appointable through those rows; its own
      // name (`References`, `Rules`) is no grant at all since D-275.
      const mayAppoint =
        isAdmin &&
        (subRoles
          ? subRoles.some((sub) => GRANTABLE_ROLES.includes(sub.role))
          : !!token && GRANTABLE_ROLES.includes(spec.role))
      if (!subRoles) {
        return {
          key: `role:${spec.role}`,
          title: spec.title,
          seats: ownSeats,
          target: mayAppoint ? { kind: 'role', role: spec.role, title: spec.title } : undefined,
        }
      }
      // PER-TAB SUB-SECTIONS ONLY (owner, 2026-08-31): the whole-screen role
      // is gone from this screen outright, and since D-275 it grants nothing
      // server-side either.
      const seatsByRole: RoleSeats[] = subRoles.map((sub) => ({
        role: sub.role,
        roles: sub.roles,
        label: sub.label,
        seats: seatsFor(...sub.roles),
      }))
      // Every References tab is both LISTED and APPOINTED here since
      // 2026-09-14 (owner: "for the sake of simplicity, let's just make
      // Vehicles and Transportation Categories just work like other
      // References"), so `subRoles` above carries all five.
      return {
        key: `role:${spec.role}`,
        title: spec.title,
        // The flat list stays the union, so the live-count badge and the Find
        // box still match every holder in the block — the same contract the
        // company cards keep between `seats` and `seatsByRole`.
        seats: seatsByRole.flatMap((rs) => rs.seats),
        seatsByRole,
        target: mayAppoint
          ? {
              kind: 'role',
              role: spec.role,
              title: spec.title,
              // Per-tab roles ONLY — no "Every tab" shortcut (owner, 2026-08-31).
              roleChoices: subRoles.map((sub) => sub.role),
            }
          : undefined,
      }
    },
    // `revokeRoleRow` reaches only module scope now that the toast and the
    // busy flag have moved out of it, so it is no longer a dependency the rule
    // has to be talked out of (QA wave B1, B1-10 — the disable directive that
    // used to sit here went with the closure it excused).
    [roleRows, isAdmin],
  )

  const rows = useMemo(
    () => (assignmentRows ?? []).filter((a) => a.workspace === WS && a.action === ASSIGNMENT_ACTION.appoint),
    [assignmentRows],
  )

  const assignmentSeats = useCallback(
    (forRows: Assignment[]): Seat[] =>
      forRows
        // CURRENT HOLDERS ONLY (owner, 2026-09-14). A toggle offered the ended
        // ones for one round and was dropped: this screen answers "who holds
        // what now", and an ended assignment is history, which belongs wherever
        // history is read rather than mixed into the answer. Nothing about the
        // data changed — `retired_at` is still stored and still returned.
        .filter(isLive)
        .map((a) => {
          const live = isLive(a)
          return {
            key: a.id,
            name: a.user_name ?? a.user_upn ?? '(unknown)',
            email: a.user_upn,
            roleLabel: assignmentRoleLabel(a.role),
            started: startedOn(a),
            live,
            canRemove: live && isAdmin,
            kind: 'assignment' as const,
            remove: () => deactivateAssignment(a),
            person: {
              id: a.user_id,
              upn: a.user_upn,
              name: a.user_name ?? a.user_upn ?? '(unknown)',
              email: a.user_upn,
            },
          }
        })
        // Every seat here is current now, so the old live-first tiebreak is
        // gone with the ended ones: by role label, then by name.
        .sort(
          (x, y) =>
            (x.roleLabel ?? '').localeCompare(y.roleLabel ?? '') || x.name.localeCompare(y.name),
        ),
    // `retireAssignment` reaches only module scope now — see the roleRows memo
    // above for why the disable directive that used to sit here is gone.
    [isAdmin],
  )

  // Every active company in the master gets its own group, ordered by name —
  // and NEITHER ANCHOR gets one. `Other` lost its card on 2026-08-11 (issue 1)
  // because there is no catch-all TEAM: authority keys on the company id, so
  // the `Other` stand-in for companies without a dedicated team went with the
  // teams. `GET /companies` now omits both anchors outright (D-132,
  // 2026-09-22), so neither reaches `companies` here at all.
  //
  // THE CATCH-ALL EXPANSION IS GONE TOO (D-131, 2026-09-22), and this comment
  // said it survived the card until then. It does not: `Other` was never a
  // company (D-134) and the 21 companies it used to stand in for each have a
  // real group here with real holders in it.
  //
  // OVERSIGHT APPROVAL IS NOT IN THESE GROUPS. It joined each company's group
  // during the per-company tier of 2026-08-10; D-124 (2026-09-15) retired that
  // and the seat is org-wide again, so it renders as ONE group —
  // `oversightGroup`, built further down — and an org-wide row, which names no
  // company, matches no company's filter here.
  const companyGroups: Group[] = useMemo(
    () =>
      companies.map((c) => {
        // The Analytics seats name a company too, but they are held on the
        // Analytics screen's own cards (`analyticsGroups`, D-258), not here.
        const forCompany = rows.filter(
          (a) => (a.company_id ?? '') === c.id && !ANALYTICS_ASSIGNMENT_ROLES.includes(a.role),
        )
        // Tier order, so the dialog's picker reads the way the card does.
        const roleChoices: AssignmentRole[] = [
          ASSIGNMENT_ROLES.director,
          ASSIGNMENT_ROLES.controller,
          ASSIGNMENT_ROLES.departmentHead,
        ]
        // Manager and Director Approval are the default path (D-336), and
        // every required step waits when nobody holds it, so a company missing
        // EITHER seat strands every invoice no rule routes elsewhere (D-336,
        // amended 2026-10-09; it used to warn only when both were empty). A
        // Controller seat stands in for neither: only an invoice a rule
        // requiring Controller Approval matches goes there (D-303).
        const missingRoles = DEFAULT_PATH_APPROVAL_ROLES.filter(
          (role) => !forCompany.some((a) => isLive(a) && a.role === role),
        )
        // Owner item 13, 2026-08-13: one sub-section per tier, in a fixed
        // order, so a role with zero holders is a visible empty state rather
        // than an omission from a single flat list. Director and Department
        // Head on every company (AP/AR too until D-221).
        // OVERSIGHT IS NOT HERE ANY MORE (owner, 2026-09-15): "There is no
        // single-company Oversight seat. The Oversight Approval role is for All
        // companies." It has its own centralized group below, beside the five
        // internal steps, and the database refuses an oversight row that names
        // a company at all. This reverses issue 2 of 2026-08-10, which had made
        // it a per-company tier.
        // Controller Approval between the two (D-303): it reads beside
        // Manager Approval, whose alternative it is, and above it, as the
        // Pipeline pop-up draws it.
        const tierOrder: AssignmentRole[] = [
          ASSIGNMENT_ROLES.director,
          ASSIGNMENT_ROLES.controller,
          ASSIGNMENT_ROLES.departmentHead,
        ]
        const seatsByRole: RoleSeats[] = tierOrder.map((role) => ({
          role,
          label: assignmentRoleLabel(role),
          seats: assignmentSeats(forCompany.filter((a) => a.role === role)),
        }))
        return {
          key: `company:${c.id}`,
          title: c.name,
          seats: assignmentSeats(forCompany),
          seatsByRole,
          target: isAdmin ? { kind: 'assignment', roleChoices, companyId: c.id, title: c.name } : undefined,
          // Shown to a viewer who may appoint (docs/app.md §6; QA 2026-10-04
          // B-04), which is an administrator alone since 2026-10-07.
          warnEmpty: noApproverWarning(isAdmin, missingRoles.map(assignmentRoleLabel)),
          missingRoles,
        }
      }),
    [companies, rows, isAdmin, assignmentSeats],
  )

  const adminGroups = useMemo(() => ADMIN_STEP_GROUPS.map(roleGroup), [roleGroup])
  // Owner, 2026-09-03: References, Rules and Analytics off the Admin screen
  // and onto their own. Same `roleGroup` builder as the other two — the split
  // is which array a group is listed in, nothing about how a group works.
  const dataGroups = useMemo(() => DATA_STEP_GROUPS.map(roleGroup), [roleGroup])
  const accountingGroups = useMemo(() => ACCOUNTING_STEP_GROUPS.map(roleGroup), [roleGroup])
  // Approvals: the one org-wide Oversight group, then the per-company groups.
  // Oversight Approval had no standalone box between 2026-08-10 (issue 2) and
  // D-124 (2026-09-15) — it was company-scoped then, and joined each company's
  // group above. D-124 made the seat org-wide and gave it its own box back,
  // which is `oversightGroup` immediately below. Not a closed list — see
  // docs/questions.md §4.3 ("Approvals' contents are not a closed list") for
  // what a follow-on task would change and why it isn't named yet.
  /**
   * OVERSIGHT APPROVAL, ONE GROUP, ORG-WIDE (owner, 2026-09-15; a scope rather
   * than a company called `All` since D-134, 2026-09-22).
   *
   * It reads like the five centralized internal steps — one holder set covering
   * every company, rendering `All` in the Company column — but it is an
   * ASSIGNMENT rather than a `user_role` grant, so it cannot be built by
   * `roleGroup` above, which seats from the roster.
   *
   * Admin-only to appoint, as every seat on this tab is (2026-10-07).
   */
  const oversightGroup: Group = useMemo(() => {
    const seats = assignmentSeats(rows.filter((a) => a.role === ASSIGNMENT_ROLES.oversight))
    return {
      key: 'role:OversightApproval',
      title: 'Oversight Approval',
      seats,
      // NAMED BY ITS `assignment_role` TOKEN, not by the group key (QA wave B1,
      // B1-01). `rowsOf` below matches a row against the target's
      // `roleChoices`, and those are `assignment_role` values — `oversight`.
      // A group with no `seatsByRole` takes its role from the key instead, so
      // this row called itself `OversightApproval`, matched no choice, and the
      // dialog said "You do not have permission to change this assignment." to
      // an administrator. The per-holder Remove buttons kept working, because
      // those are built in `assignmentSeats` and never consult the target — so
      // the one organization-wide seat could be emptied and not refilled.
      seatsByRole: [{ role: ASSIGNMENT_ROLES.oversight, label: 'Oversight Approval', seats }],
      target: isAdmin
        ? {
            kind: 'assignment',
            roleChoices: [ASSIGNMENT_ROLES.oversight],
            // NO COMPANY, DELIBERATELY (D-134, 2026-09-22). This used to send
            // the literal `All`, which was a row in `companies` that was not a
            // company. The seat is org-wide, so the appointment names none —
            // `company_id: null` on the wire — and the server refuses an
            // oversight appointment that names one.
            companyId: undefined,
            title: ORG_WIDE_COMPANY_LABEL,
          }
        : undefined,
    }
  }, [rows, isAdmin, assignmentSeats])

  // Approvals: the one centralized Oversight group, then the per-company tiers.
  const approvalGroups = [oversightGroup, ...companyGroups]

  /**
   * ANALYTICS, ONE CARD PER COMPANY (owner, 2026-10-01 — D-258): "Under
   * Analytics, I want to establish company-specific roles for both Dashboard and
   * Reporting. Neither should remain all-inclusive across all companies."
   *
   * Built like the Invoices screen's company cards, so each company reads as
   * two rows — Dashboard and Reporting — with the Company column beside them,
   * and appointing picks the person and the tab for that company. Admin-only to
   * appoint and to remove, as every seat on this tab is (2026-10-07).
   */
  const analyticsGroups: Group[] = useMemo(
    () =>
      companies.map((c) => {
        const forCompany = rows.filter(
          (a) => (a.company_id ?? '') === c.id && ANALYTICS_ASSIGNMENT_ROLES.includes(a.role),
        )
        const seatsByRole: RoleSeats[] = ANALYTICS_SECTIONS.map((section) => {
          const role = ANALYTICS_SECTION_ASSIGNMENT_ROLE[section]
          return {
            role,
            label: assignmentRoleLabel(role),
            seats: assignmentSeats(forCompany.filter((a) => a.role === role)),
          }
        })
        return {
          key: `company:${c.id}`,
          title: c.name,
          seats: assignmentSeats(forCompany),
          seatsByRole,
          target: isAdmin
            ? {
                kind: 'assignment',
                roleChoices: ANALYTICS_SECTIONS.map((section) => ANALYTICS_SECTION_ASSIGNMENT_ROLE[section]),
                companyId: c.id,
                title: c.name,
              }
            : undefined,
        }
      }),
    [companies, rows, isAdmin, assignmentSeats],
  )

  // THE APP'S OWN NAV TABS (owner, 2026-09-14). Each screen holds the roles
  // that govern one tab of the application, which is the question an admin is
  // actually holding — rather than the migration-era split by where a role came
  // from. Nothing about which roles exist, or who may grant them, changed: the
  // same four builders feed these five screens.
  const byRoleKey = (role: Role) => dataGroups.filter((g) => g.key === `role:${role}`)
  const groupsByScreen: Record<AssignmentScreen, Group[]> = {
    // The five internal step roles are centralized — one holder set for every
    // company — and the approval tiers are per company. Both govern the
    // Invoices tab, so both are listed here, the centralized ones first.
    invoices: [...accountingGroups, ...approvalGroups],
    // No role groups: the Notifications tab is a list of recipients (D-310).
    notifications: [],
    analytics: analyticsGroups,
    references: byRoleKey('References'),
    rules: byRoleKey('Rules'),
    assignments: adminGroups,
  }

  // THE OPEN ROW, AS IT NOW STANDS. `manageRow` is the row as it was when it
  // opened; after an Apply that was partly refused the pop-up stays open, and it
  // has to show what actually landed (D-341).
  const liveManageRow =
    manageRow && activeScreen
      ? (rowsOf(groupsByScreen[activeScreen]).find((r) => r.key === manageRow.key) ?? manageRow)
      : manageRow

  // Admin, Data and Accounting all read `user_roles` — they are three listings
  // of the same roster. Approval reads TWO independent sources —
  // `assignments` and the company master — both modeled here so neither can
  // silently pop groups in after the other resolves; `pageState` is `pending`
  // until both are loaded and `failed` if either is.
  // Invoices reads THREE sources — `user_roles` for the internal steps,
  // `assignments` and the company master for the tiers — so it is `pending`
  // until all three are loaded and `failed` if any is.
  const screenLoadState = (screen: AssignmentScreen): ReadState<unknown>[] =>
    assignmentScreenReads<ReadState<unknown>>(screen, {
      userRoles: userRolesState,
      assignments: assignmentsState,
      companies: companiesState,
    })

  const term = search.trim().toLowerCase()
  // FILTERING IS ON ROWS, not groups (owner, 2026-09-14): "so I can filter for
  // Manager Approval or for PACE Healthcare Transportation". Both are a column
  // now, so both are a dropdown, and the search box matches either column, the
  // company's id (D-348), or a person in the row.
  const matchesRow = (r: AssignmentRow) =>
    (roleFilter.length === 0 || roleFilter.includes(r.role)) &&
    (companyFilter.length === 0 || (r.company !== undefined && companyFilter.includes(r.company))) &&
    (personFilter.length === 0 || r.seats.some((m) => m.live && personFilter.includes(m.name))) &&
    (approverFilter === '' || !!r.flagged) &&
    (!term ||
      roleLabel(r).toLowerCase().includes(term) ||
      (r.company ?? '').toLowerCase().includes(term) ||
      (r.companyId ?? '').toLowerCase().includes(term) ||
      r.seats.some(
        (m) => m.name.toLowerCase().includes(term) || (m.email ?? '').toLowerCase().includes(term),
      ))

  // No screen to show — render nothing rather than defaulting to one the
  // session may not actually hold anything on (`App.tsx` also gates the tab
  // itself on `canOpenAssignments(session)`, so this is a defensive second
  // check for the resync window above, not the only one).
  if (activeScreen === null) return null

  const screenTabs = (
    <PageTabs
      tabs={visibleScreens.map((sc) => ({ value: sc, label: ASSIGNMENT_SCREEN_TITLE[sc] }))}
      selectedValue={activeScreen}
      onSelect={setActiveScreen}
      ariaLabel="Assignments screens"
    />
  )

  // NOTIFICATIONS IS NOT A ROLE LIST (owner, 2026-10-07 — D-310): it is who receives
  // each notification, per company — Rules -> Upload Notifications until then. It
  // brings its own filter and its own editor.
  if (activeScreen === 'notifications') {
    return (
      <div>
        {screenTabs}
        <ScreenIntro>{NOTIFICATIONS_TAB_INTRO}</ScreenIntro>
        <NotificationRecipientsManager />
      </div>
    )
  }

  const currentReads = screenLoadState(activeScreen)
  const currentStatus = pageState(...currentReads)
  // Approvals can fail on BOTH of its sources at once, and each is
  // independently actionable (an assignments-read failure and a companies-read
  // failure point at different requests) — collect every failed message rather
  // than showing only the first and burying the other.
  const currentErrors = currentReads
    .filter((r): r is { kind: 'failed'; error: string } => r.kind === 'failed')
    .map((r) => r.error)
  const currentError = currentErrors.length > 0 ? currentErrors.join(' ') : 'Could not load this screen.'
  const currentGroups = groupsByScreen[activeScreen]
  const allRows = rowsOf(currentGroups)
  const stepOf = (role: string) => allRows.find((r) => r.role === role)?.step
  const filtered = allRows.filter(matchesRow)
  // ROLE BY DEFAULT on every screen (`ROLE_SORT`). A header click departs from
  // it; with no sort at all the rows keep the order `rowsOf` builds.
  const visible = sort.state
    ? sortRows(filtered, sort.state, {
        [sort.state.key]: (r: AssignmentRow) => rowSortValue(r, sort.state?.key ?? ''),
      })
    : filtered
  // The dropdowns offer what this screen actually holds, in the order the rows
  // are in — so Role reads down the workflow on Invoices and down the tab bar
  // on References, rather than alphabetically past both.
  // SELECT ALL SELECTS WHAT IS ON SCREEN — the filtered, sorted rows — not every
  // row the screen holds. A tick that reaches past what the reader can see is
  // how a bulk action surprises someone.
  const selectableVisible = visible.filter((r) => r.fill)
  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every((r) => selectedRows.has(r.key))
  const toggleAllVisible = () => {
    const keys = selectableVisible.map((r) => r.key)
    setSelectedRows((prev) => (keys.every((k) => prev.has(k)) ? new Set() : new Set(keys)))
  }
  const roleOptions = [...new Map(allRows.map((r) => [r.role, roleLabel(r)])).entries()]
    .sort((a, b) => rowSortValue({ role: a[0], step: stepOf(a[0]) } as AssignmentRow, 'role').localeCompare(
      rowSortValue({ role: b[0], step: stepOf(b[0]) } as AssignmentRow, 'role'),
    ))
    .map(([value, label]) => ({ value, label }))
  // THE COLUMN EXISTS WHERE THE ROWS HAVE COMPANIES (owner, 2026-09-22, D-135),
  // AND ON REFERENCES, RULES AND ASSIGNMENTS, where every cell reads `All`
  // (owner, 2026-10-07 — `ALL_COMPANY_SCREENS`). The row model is unchanged:
  // `rowsOf` still leaves `company` undefined on an organization-wide row, and
  // the cell draws `ALL_COMPANIES` for it, as it always has on Invoices. The
  // FILTER DROPDOWN still rides on real companies (`companyValues` below), so
  // a screen where every row reads `All` offers no Company filter, and the
  // search box does not claim to search a company there either.
  const rowsHaveCompany = allRows.some((r) => r.company !== undefined)
  const showCompany = rowsHaveCompany || ALL_COMPANY_SCREENS.includes(activeScreen)
  const companyValues = [...new Set(allRows.map((r) => r.company).filter((c): c is string => c !== undefined))]
  const companyOptions = companyValues.map((v) => ({ value: v, label: v }))
  // A-Z, unlike Role and Company: those two carry a meaningful order of their
  // own (the workflow, the tab bar) and people do not — a reader looking for a
  // name looks for it alphabetically. Live seats only, so the list offers only
  // answers that can match something.
  const personOptions = [...new Set(allRows.flatMap((r) => r.seats.filter((m) => m.live).map((m) => m.name)))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map((v) => ({ value: v, label: v }))
  // Counted over EVERY company, not the filtered view: the banner reports the
  // state of the roster, and a search that hides thirteen of them has not fixed
  // thirteen of them. `warnEmpty` is the existing rule, viewer-scoped included
  // — it is set only where the viewer may appoint, so nobody is warned about
  // companies they cannot staff.
  const noApproverCount =
    activeScreen === 'invoices' ? currentGroups.filter((g) => g.warnEmpty).length : 0
  /** The warning's button: exactly the companies it counts — every role, no
      search, nobody picked, no company picked (owner, 2026-10-07). */
  const showNoApprovers = () => {
    setSearch('')
    setRoleFilter([])
    setPersonFilter([])
    setCompanyFilter([])
    setApproverFilter(NO_APPROVERS)
  }
  const noApproversShown =
    approverFilter === NO_APPROVERS &&
    search === '' &&
    roleFilter.length === 0 &&
    personFilter.length === 0 &&
    companyFilter.length === 0

  // Cross-screen Find hint: when the term matches nothing on THIS screen but
  // does match a group on another VISIBLE screen, name it. Suppressed unless
  // that other screen's data is fully `ready` — `pending` data is empty and
  // would silently miss a real match, and a `failed` read means we cannot
  // know either way, so both guess in the wrong direction if allowed through.
  const otherScreenHints =
    term && visible.length === 0
      ? visibleScreens.filter(
          (sc) =>
            sc !== activeScreen &&
            pageState(...screenLoadState(sc)) === 'ready' &&
            rowsOf(groupsByScreen[sc]).some(
              (r) =>
                r.role.toLowerCase().includes(term) ||
                (r.company ?? '').toLowerCase().includes(term) ||
                r.seats.some(
                  (m) =>
                    m.name.toLowerCase().includes(term) ||
                    (m.email ?? '').toLowerCase().includes(term),
                ),
            ),
        )
      : []

  return (
    <div>
      {/* The screen selector, in the header (`components/PageTabs.tsx`). It
          always renders, even with only one visible screen — a fixed set of
          controls beats layout that jumps depending on who is looking. */}
      {screenTabs}
      <ScreenIntro>{SCREEN_SUBHEADER.Assignments}</ScreenIntro>

      {/* THE SAME FILTER CONTROL REFERENCES AND RULES CARRY (owner,
          2026-09-14), and in the same place: the header's top-right cluster,
          not a row of its own between the tabs and the cards. The Find box and
          the loose "Show retired and rejected" checkbox were this screen's own
          furniture; they do the same job as that button and looked nothing like
          it. Role and Company ride in as extra dropdowns, and the retired
          toggle as the popover's one checkbox. */}
      <ScreenActions>
        <ListFilters
          term={search}
          onTermChange={setSearch}
          searchPlaceholder={rowsHaveCompany ? 'Role, company, or person…' : 'Role or person…'}
          ariaLabel="Assignments"
          selects={[
            { multiple: true, label: 'Roles', values: roleFilter, onChange: setRoleFilter, options: roleOptions },
            ...(personOptions.length > 0
              ? [
                  {
                    multiple: true as const,
                    label: 'Assigned To',
                    values: personFilter,
                    onChange: setPersonFilter,
                    options: personOptions,
                  },
                ]
              : []),
            ...(companyValues.length > 1
              ? [
                  {
                    multiple: true as const,
                    label: 'Companies',
                    values: companyFilter,
                    onChange: setCompanyFilter,
                    options: companyOptions,
                  },
                ]
              : []),
            ...(activeScreen === 'invoices' && (noApproverCount > 0 || approverFilter !== '')
              ? [
                  {
                    label: 'Approvers',
                    value: approverFilter,
                    onChange: (v: string) => setApproverFilter(v === NO_APPROVERS ? NO_APPROVERS : ''),
                    options: [{ value: NO_APPROVERS, label: 'Missing' }],
                  },
                ]
              : []),
          ]}
        />
      </ScreenActions>

            {/* The one urgent fact, said ONCE. It was a yellow sentence
                inside each affected company's card, which meant reading
                thirty cards to learn there were twenty — and the count is the
                part that decides whether anyone acts today. Shown only to a
                viewer who may appoint somewhere, the same rule the per-card
                sentence already followed. Company Roles is the only screen
                with anything urgent to say, which is why this is the one
                thing on it the other three do not have. */}
            {noApproverCount > 0 && (
              <WarningBanner
                action={
                  noApproversShown ? undefined : (
                    <Button appearance="secondary" onClick={showNoApprovers}>
                      Show These Companies
                    </Button>
                  )
                }
              >
                <strong>
                  {noApproverCount === 1
                    ? '1 company is missing required approvers.'
                    : `${noApproverCount} companies are missing required approvers.`}
                </strong>{' '}
                {/* Since D-336 (owner, 2026-10-09) every step is required, so an
                    invoice waits rather than moving on past an empty seat, and
                    one empty default-path seat is enough to strand it. */}
                Invoices for {noApproverCount === 1 ? 'this company' : 'these companies'} will
                wait at Manager Approval or Director Approval wherever nobody is assigned, until
                someone is.
              </WarningBanner>
            )}

      {/* NOTHING UNTIL SOMETHING IS TICKED (owner, 2026-09-15: "I liked it
          better when our tables were relatively flush underneath the sub-tabs
          ... put the Select All checkbox in the top-left corner within the
          table, unlabeled, and drop 'Select all' while hiding the action
          buttons until rows are actually selected").

          It was one row carrying a labelled Select all and two greyed-out
          buttons, on every visit — itself a collapse of the two rows that
          preceded it (owner, 2026-09-14: the spacing around Select all,
          "0 selected" and Assign Selected "is mostly dead and awkward"). Select
          all is the table's own header cell now, above the column of row
          checkboxes it controls, and this strip appears only when it has
          something to act on. The count still rides in the button, which is
          where it is read anyway ("Assign 3 Selected").

          References and Rules took the same treatment in the same change
          (`components/BulkActions.tsx`), so the three screens still match. */}
      {currentStatus === 'ready' && selectedRows.size > 0 && (
        <div className={s.selBar}>
          {/* ONE BUTTON (owner, 2026-10-09 — D-341). The red Unassign From and
              green Assign to pair (D-198) became one pop-up that does both, so
              the strip carries one control for it. */}
          <Button appearance="primary" onClick={openBulk}>
            {`Edit ${selectedRows.size} Selected`}
          </Button>
        </div>
      )}

      {currentStatus === 'pending' ? (
        <Spinner label="Loading assignments…" />
      ) : currentStatus === 'failed' ? (
        <ErrorBanner message={currentError} />
      ) : (
        <div className={s.groups}>
          {visible.length === 0 ? (
            <Body1 className={s.none}>
              Nothing matches the current filters.
              {otherScreenHints.length > 0 && (
                <span className={s.hint}>
                  It matches on {otherScreenHints.map((sc) => ASSIGNMENT_SCREEN_TITLE[sc]).join(' and ')}.
                </span>
              )}
            </Body1>
          ) : (
            <>
              <AssignmentTable
                rows={visible}
                ariaLabel={`${ASSIGNMENT_SCREEN_TITLE[activeScreen]} assignments`}
                showCompany={showCompany}
                sort={sort}
                selected={selectedRows}
                onToggle={(key) =>
                  setSelectedRows((prev) => {
                    const next = new Set(prev)
                    if (!next.delete(key)) next.add(key)
                    return next
                  })
                }
                allSelected={allVisibleSelected}
                anySelected={selectedRows.size > 0}
                selectableCount={selectableVisible.length}
                onToggleAll={toggleAllVisible}
                onOpen={openManage}
                s={s}
              />
            </>
          )}
        </div>
      )}

      {/* THE ROW'S OWN EDITOR — a People Field (owner, 2026-10-09 — D-341).
          Everyone who holds the row is a name tag; typing in the same box adds
          another, and the ✕ on a tag takes that person off. Every change is
          held until Apply, so several people come and go in one trip, and
          "Are you sure?" is asked once, only when someone is being removed.

          It opens on `manageRow` rather than on `target`, because a row the
          viewer may not appoint into still has holders worth reading; that row
          has no `target` and the field offers no search. */}
      <AppDialog open={manageRow !== null} onOpenChange={(_, d) => !d.open && closeDialog()}>
        {/* Hidden while "Are you sure?" is up (owner, 2026-10-09: "This looks
            awkward. Can we hide Edit Assignments"): the question opens over it in
            its own portal, so only the question shows. */}
        <DialogSurface ref={setDialogSurface} style={confirmOpen ? { visibility: 'hidden' } : undefined}>
          <FrozenWhileClosed open={manageRow !== null}><DialogBody>
            {/* ONE TITLE FOR EVERY ASSIGNMENTS POP-UP (owner, 2026-10-09). The
                row it opened on, which was the title, labels the field. */}
            <DialogTitle>Edit Assignments</DialogTitle>
            <DialogContent className={f.beforeActions}>
              <div className={mergeClasses(s.form, f.stack)}>
                <ErrorBanner message={dialogError} />
                {/* The row's name is the field's label (owner, 2026-10-11 —
                    D-348), where "Staff" was, so it is not said twice. Every
                    word capitalized, as every pop-up label is (D-351). */}
                {liveManageRow && (
                  <PeopleField
                    label={rowLabel(liveManageRow)}
                    tags={[
                      ...liveManageRow.seats
                        .filter((m) => m.live && !drops.has(m.key))
                        .map<PersonTag>((m) => ({
                          key: m.key,
                          name: m.name,
                          detail: [m.email, m.started].filter(Boolean).join(' · '),
                          tone: 'held',
                          removable: m.canRemove && !!m.remove,
                        })),
                      ...adds.map<PersonTag>((u) => ({
                        key: `new:${u.id}`,
                        name: userOf(u).name,
                        detail: userOf(u).email,
                        tone: 'new',
                        removable: true,
                      })),
                    ]}
                    removing={liveManageRow.seats
                      .filter((m) => drops.has(m.key))
                      .map((m) => ({ key: m.key, name: m.name }))}
                    onRemove={stageRemove}
                    onUndo={undoRemove}
                    search={searchStaff}
                    onAdd={(option) =>
                      stageAdd(
                        option,
                        (user) =>
                          liveManageRow.seats.find((m) => m.live && m.person?.id === user.id)?.key ?? null,
                      )
                    }
                    mountNode={dialogSurface}
                    disabled={submitting}
                    readOnly={target === null}
                  />
                )}
                {target === null && (
                  <Caption1 className={s.atNote}>
                    You do not have permission to change this assignment.
                  </Caption1>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeDialog} disabled={submitting}>
                {target === null ? 'Close' : 'Cancel'}
              </Button>
              {target !== null && (
                <Button
                  appearance="primary"
                  onClick={() => void saveRow()}
                  disabled={submitting || adds.length + drops.size === 0}
                >
                  {submitting ? 'Applying…' : 'Apply'}
                </Button>
              )}
            </DialogActions>
          </DialogBody></FrozenWhileClosed>
          {/* ARE YOU SURE? — the standard pop-up (B-07), nested in this
              dialog so it opens over it. */}
          <DecisionDialog
            open={confirmOpen && manageRow !== null}
            title="Are you sure?"
            body={confirmText}
            requireReason={false}
            confirmLabel="Apply"
            submitting={submitting}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => void saveRow(true)}
          />
        </DialogSurface>
      </AppDialog>

      {/* EDIT N SELECTED (owner, 2026-10-09 — D-341). One pop-up for the
          ticked rows, where Assign to N Selected and Unassign From N Selected
          were two. Its People Field holds everyone who holds ANY of the rows:
          a plain tag holds them all, a dashed "2 of 5" tag holds some.
          Clicking a dashed tag's name adds that person to the rest; its ✕ takes
          them off the rows they hold; a new name is added to every row. Each
          row still writes through its own target, so the authority mirror
          applies per row exactly as it does for one. */}
      <AppDialog open={bulkOpen} onOpenChange={(_, d) => !d.open && closeBulk()}>
        <DialogSurface ref={setDialogSurface} style={confirmOpen ? { visibility: 'hidden' } : undefined}>
          <FrozenWhileClosed open={bulkOpen}><DialogBody>
            <DialogTitle>Edit Assignments</DialogTitle>
            <DialogContent className={f.beforeActions}>
              <div className={mergeClasses(s.form, f.stack)}>
                <ErrorBanner message={dialogError} />
                {bulkOpen && (() => {
                  const rows = selectedRowsForBulk()
                  const people = bulkPeople(rows)
                  return (
                    // No one row's name to label it with: no label (D-348).
                    <PeopleField
                      ariaLabel={`Staff for ${rows.length} selected roles`}
                      tags={[
                        ...people
                          .filter((p) => !drops.has(p.key))
                          .map<PersonTag>((p) => {
                            const all = p.rowKeys.size === rows.length
                            const completing = completes.has(p.key)
                            return {
                              key: p.key,
                              name: p.name,
                              detail: p.email,
                              tone: completing ? 'new' : all ? 'held' : 'partial',
                              count: all || completing ? undefined : `${p.rowKeys.size} of ${rows.length}`,
                              removable: p.seats.some(({ seat }) => seat.canRemove && !!seat.remove),
                              onComplete:
                                all || completing || !p.person
                                  ? undefined
                                  : () => setCompletes((prev) => new Set(prev).add(p.key)),
                            }
                          }),
                        ...adds.map<PersonTag>((u) => ({
                          key: `new:${u.id}`,
                          name: userOf(u).name,
                          detail: userOf(u).email,
                          tone: 'new',
                          removable: true,
                        })),
                      ]}
                      removing={people.filter((p) => drops.has(p.key)).map((p) => ({ key: p.key, name: p.name }))}
                      onRemove={stageRemove}
                      onUndo={undoRemove}
                      search={searchStaff}
                      onAdd={(option) =>
                        stageAdd(option, (user) => people.find((p) => p.person?.id === user.id)?.key ?? null)
                      }
                      mountNode={dialogSurface}
                      disabled={submitting}
                    />
                  )
                })()}
                {/* The owner's two sentences (2026-10-09), one per line. */}
                <Caption1 className={s.bulkScope}>
                  Click a person's name to add that person to all selected roles.
                  <br />
                  Click a person's "x" to remove that person from all selected roles.
                </Caption1>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={closeBulk} disabled={submitting}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={() => void saveBulk()}
                disabled={submitting || adds.length + drops.size + completes.size === 0}
              >
                {submitting ? 'Applying…' : 'Apply'}
              </Button>
            </DialogActions>
          </DialogBody></FrozenWhileClosed>
          <DecisionDialog
            open={confirmOpen && bulkOpen}
            title="Are you sure?"
            body={confirmText}
            requireReason={false}
            confirmLabel="Apply"
            submitting={submitting}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => void saveBulk(true)}
          />
        </DialogSurface>
      </AppDialog>
    </div>
  )
}


/**
 * ONE TABLE, EVERY ASSIGNMENTS SCREEN (2026-09-14).
 *
 * Columns: the pipeline step (Invoices only), the role, the company it applies
 * to, and who holds it. Every screen is the same table at a different length —
 * 1 row on Assignments, 2 on Analytics, 4 on Rules, 5 on References — six tabs
 * in five rows, Vehicles & Transportation being one (owner, 2026-09-14) — and
 * on Invoices the five centralized steps at `All` plus every company's tiers.
 *
 * IT BEHAVES LIKE THE LISTS ON REFERENCES AND RULES, which is the whole of its
 * design: headers sort on click, the ROW opens its own editor, and the row
 * carries no per-row buttons of its own. What it adds beyond them is a checkbox
 * column, because one person is very often given the same role on many
 * companies at once and doing that a row at a time is the job this screen is
 * for.
 */
function AssignmentTable({
  rows,
  ariaLabel,
  showCompany,
  sort,
  selected,
  onToggle,
  allSelected,
  anySelected,
  selectableCount,
  onToggleAll,
  onOpen,
  s,
}: {
  rows: readonly AssignmentRow[]
  ariaLabel: string
  /** The Company column: on Invoices and Analytics, whose rows name companies
      (D-135, D-258), and on References, Rules and Assignments, where every
      cell reads `All` (owner, 2026-10-07 — `ALL_COMPANY_SCREENS`). Notifications is
      its own table and has one too. */
  showCompany: boolean
  /** The sort state lives with the screen, not the table: it resets with the
      tab, because Invoices' default is Step and the other four have none. */
  sort: ReturnType<typeof useTableSort<string>>
  selected: ReadonlySet<string>
  onToggle: (key: string) => void
  /** Select all / unselect all, over the rows this table is showing that can
      actually be acted on (owner, 2026-09-15 — it moved out of the screen's
      toolbar and into the header cell above the column it controls). */
  allSelected: boolean
  anySelected: boolean
  selectableCount: number
  onToggleAll: () => void
  onOpen: (row: AssignmentRow) => void
  s: ReturnType<typeof useStyles>
}) {
  // The SAME density, shaded header and clickable row References and Rules
  // wear. Three admin lists in one app agreeing is the whole reason those
  // modules are shared rather than copied.
  const roomy = useRoomyTableClass()
  const clickable = useClickableRowClass()
  return (
    <div className={s.atScroll}>
      <Table size="small" aria-label={ariaLabel} className={mergeClasses(s.atTable, roomy)}>
        <TableHeader>
          <TableRow>
            {/* SELECT ALL, UNLABELLED, IN THE CORNER (owner, 2026-09-15: "put
                the Select All checkbox in the top-left corner within the table,
                unlabeled, and drop 'Select all'"). It was a labelled checkbox
                in a strip above the table; here it sits directly above the
                column of row checkboxes it controls, which is where the word
                was doing its explaining anyway.

                `mixed` on a partial selection — a plain unticked box beside
                three ticked rows would say "nothing is selected". */}
            <TableHeaderCell style={{ width: 44 }}>
              <Checkbox
                checked={allSelected ? true : anySelected ? 'mixed' : false}
                onChange={onToggleAll}
                disabled={selectableCount === 0}
                aria-label={allSelected ? `Unselect all ${ariaLabel}` : `Select all ${ariaLabel}`}
              />
            </TableHeaderCell>
            <SortableHeaderCell
              style={{ width: '28%' }}
              sortDirection={sort.directionFor('role')}
              onSort={() => sort.toggle('role')}
            >
              Role
            </SortableHeaderCell>
            {showCompany && (
              <SortableHeaderCell
                style={{ width: '24%' }}
                sortDirection={sort.directionFor('company')}
                onSort={() => sort.toggle('company')}
              >
                Company
              </SortableHeaderCell>
            )}
            <SortableHeaderCell
              sortDirection={sort.directionFor('assigned')}
              onSort={() => sort.toggle('assigned')}
            >
              Assigned to
            </SortableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <AssignmentTableRow
              key={r.key}
              row={r}
              showCompany={showCompany}
              selected={selected.has(r.key)}
              onToggle={onToggle}
              onOpen={onOpen}
              clickable={clickable}
              s={s}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function AssignmentTableRow({
  row,
  showCompany,
  selected,
  onToggle,
  onOpen,
  clickable,
  s,
}: {
  row: AssignmentRow
  showCompany: boolean
  selected: boolean
  onToggle: (key: string) => void
  onOpen: (row: AssignmentRow) => void
  clickable: string
  s: ReturnType<typeof useStyles>
}) {
  return (
    <TableRow className={clickable} {...rowActivationProps(() => onOpen(row), `Manage ${rowName(row)}`)}>
      {/* THE CHECKBOX CELL SWALLOWS ITS OWN CLICK. The row is a button, so a
          click anywhere in it opens the editor — which would make ticking a box
          open a dialog over the tick. */}
      <TableCell
        className={s.atCell}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={selected}
          disabled={!row.fill}
          onChange={() => onToggle(row.key)}
          aria-label={`Select ${rowName(row)}`}
        />
      </TableCell>
      <TableCell className={s.atCell}>
        <span className={s.atRole}>{roleLabel(row)}</span>
      </TableCell>
      {showCompany && (
        <TableCell className={s.atCell}>
          {/* A ROW WITH NO COMPANY still needs a cell here: Invoices mixes the
              per-company tiers with the internal step roles and the org-wide
              Oversight seat, and on References, Rules and Assignments every row
              is organization-wide. `All` is the word for those — a word on a
              screen, never a company id. */}
          <span className={row.company === undefined ? s.atAll : undefined}>
            {row.company ?? ALL_COMPANIES}
          </span>
          {/* The banner counts these; this says which seat is empty and what
              that does, on that seat's own row (D-336, amended 2026-10-09). */}
          {row.flagNote && <Caption1 className={s.atFlag}>{row.flagNote}</Caption1>}
        </TableCell>
      )}
      <TableCell className={s.atCell}>
        {row.seats.length === 0 ? (
          <Caption1 className={s.atVacant}>Vacant</Caption1>
        ) : (
          <span className={s.atSeats}>
            {row.seats.map((m) => (
              <span key={m.key} className={mergeClasses(s.atSeat, !m.live && s.atSeatEnded)}>
                <span>{m.name}</span>
                {m.email && <Caption1 className={s.atEmail}>{m.email}</Caption1>}
              </span>
            ))}
          </span>
        )}
      </TableCell>
    </TableRow>
  )
}


/** The text a column sorts on. ROLE SORTS BY STEP FIRST, NUMERICALLY and
    zero-padded, so Step 10 follows Step 9 rather than Step 1, then by the role
    and the company; a role with no step sorts after every step. */
function rowSortValue(row: AssignmentRow, key: string): string {
  if (key === 'role') {
    const n = row.step === undefined ? 'zzz' : String(row.step).padStart(3, '0')
    return `${n} ${row.role} ${row.company ?? ''}`
  }
  if (key === 'company') return row.company ?? ''
  // An empty role sorts under a single space rather than the empty string, so
  // "Vacant" groups together rather than scattering by whatever follows it.
  return row.seats.map((m) => m.name).join(' ') || ' '
}
